import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import { PageWrap, Loader, CountUp } from "../components/common";
import { motion } from "framer-motion";
import {
  Sprout, Wallet, ChevronRight, MapPin, ShoppingCart, Tractor, Landmark,
  Users, LayoutGrid, Wheat, AlertTriangle, PartyPopper,
} from "lucide-react";
import AdaptiveHero from "../components/AdaptiveHero";
import { audio } from "../lib/audio";
import { FarmScoreCard } from "../components/dashboard/FarmScoreCard";
import { CashflowCard } from "../components/dashboard/CashflowCard";
import { WeeklyWeather } from "../components/dashboard/WeeklyWeather";
import { CropCycles } from "../components/dashboard/CropCycles";
import { MyCropRates } from "../components/dashboard/MyCropRates";
import { FieldsCarousel } from "../components/dashboard/FieldsCarousel";
import { AlertsRail } from "../components/dashboard/AlertsRail";
import { AiAdvisorRail } from "../components/dashboard/AiAdvisorRail";
import { CheckInCard } from "../components/dashboard/CheckInCard";
import { MorningBrief } from "../components/dashboard/MorningBrief";
import { PlannedCrops } from "../components/dashboard/PlannedCrops";
import TodayMode from "../components/TodayMode";

const pick = (lang, hi, en) => (lang === "English" ? en : hi);

const QUICK = [
  { to: "/farms", icon: MapPin, hi: "खेत मैप करें", en: "Map field", bg: "bg-sky-100", fg: "text-sky-600" },
  { to: "/crops", icon: Sprout, hi: "फसल चुनें", en: "Pick crop", bg: "bg-green-100", fg: "text-green-600" },
  { to: "/accounting", icon: Wallet, hi: "खर्च जोड़ें", en: "Add expense", bg: "bg-amber-100", fg: "text-[#b06a2c]" },
  { to: "/market", icon: ShoppingCart, hi: "मंडी भाव", en: "Mandi rates", bg: "bg-orange-100", fg: "text-orange-600" },
  { to: "/equipment", icon: Tractor, hi: "ट्रैक्टर/मजदूर", en: "Equipment", bg: "bg-slate-100", fg: "text-slate-600" },
  { to: "/schemes", icon: Landmark, hi: "सरकारी योजनाएं", en: "Schemes", bg: "bg-indigo-100", fg: "text-indigo-600" },
  { to: "/crm", icon: Users, hi: "समुदाय", en: "Community", bg: "bg-teal-100", fg: "text-teal-600" },
  { to: "/analytics", icon: LayoutGrid, hi: "और सेवाएं", en: "More", bg: "bg-rose-100", fg: "text-rose-600" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const { language } = useApp();
  const [data, setData] = useState(null);
  const [weather, setWeather] = useState(null);
  const [fields, setFields] = useState([]);
  const [score, setScore] = useState(null);
  const [warnings, setWarnings] = useState(null);
  const [mycrops, setMycrops] = useState(null);

  useEffect(() => {
    audio.playSonic();
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData(false));
    api.get("/farm-score").then((r) => setScore(r.data)).catch(() => {});
    api.get("/warnings").then((r) => setWarnings(r.data)).catch(() => {});
    api.get("/market/mycrops").then((r) => setMycrops(r.data)).catch(() => {});
    api.get("/data/fields").then((r) => {
      const fs = r.data || [];
      setFields(fs);
      const c = fs[0]?.center || { lat: 24.4567, lng: 74.8736 };
      api.get(`/weather?lat=${c.lat}&lng=${c.lng}`).then((w) => setWeather(w.data)).catch(() => {});
    }).catch(() => {
      api.get("/weather").then((w) => setWeather(w.data)).catch(() => {});
    });
  }, []);

  if (!data) return <Loader label="Loading your farm…" />;

  const redAlert = warnings?.alerts?.find((a) => a.severity === "red");
  const cycles = data.activeCycles || [];
  const harvest = [...cycles].sort((a, b) => (b.progress || 0) - (a.progress || 0))[0];
  const harvestDays = harvest ? Math.max(1, Math.round((100 - (harvest.progress || 0)) * 1.2)) : null;

  const stagger = { show: { transition: { staggerChildren: 0.06 } } };
  const item = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

  return (
    <PageWrap>
      {redAlert && (
        <div className="rounded-2xl bg-destructive text-destructive-foreground p-4 sm:p-5 mb-5 flex items-start gap-3 shadow-lg" data-testid="weather-red-banner">
          <AlertTriangle className="h-6 w-6 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-sm sm:text-base">{pick(language, redAlert.title_hi, redAlert.title_en)} · {pick(language, redAlert.when_hi, redAlert.when_en)}</p>
            <p className="text-xs opacity-90 mt-0.5">{pick(language, redAlert.action_hi, redAlert.action_en)}</p>
          </div>
        </div>
      )}

      {/* Adaptive time-of-day hero */}
      <div className="mb-5" data-testid="dashboard-hero">
        <AdaptiveHero name={user?.name} language={language}
          subline={pick(language, "आपकी मेहनत, आपकी सफलता 🌿", "Your effort, your success 🌿")} rain={!!redAlert} />
      </div>

      {/* Morning triage: ACT / CHECK / KNOW (morning slot only) */}
      <MorningBrief language={language} item={item} />

      {/* Planned crops — sowing-window countdown + input checklist */}
      <PlannedCrops language={language} item={item} />

      {/* Daily check-in entry (slot-aware: morning / evening) */}
      <CheckInCard language={language} item={item} />

      {/* Mode-aware Today (beginner guided task vs pro dense board) */}
      <TodayMode />

      {/* AI Command-Center advisor rail — top, most prominent */}
      <motion.div variants={stagger} initial="hidden" animate="show">
        <AiAdvisorRail language={language} item={item} />
      </motion.div>

      <motion.div variants={stagger} initial="hidden" animate="show" className="grid lg:grid-cols-3 gap-5">
        {/* MAIN COLUMN */}
        <div className="lg:col-span-2 space-y-5 min-w-0">
          <div className="grid sm:grid-cols-2 gap-4 min-w-0">
            <FarmScoreCard score={score} language={language} item={item} />

            {/* HARVEST COUNTDOWN */}
            <motion.div variants={item} className="agro-glass rounded-[22px] p-5 flex flex-col" data-testid="harvest-card">
              <div className="flex items-center gap-2 text-[#6c7a67] text-sm font-medium"><Wheat className="h-4 w-4 text-[#b06a2c]" />{pick(language, "कटाई में बाकी दिन", "Days to harvest")}</div>
              {harvestDays != null ? (
                <>
                  <p className="font-display text-[40px] font-extrabold text-[#173f1e] leading-none mt-1"><CountUp value={harvestDays} /> <span className="text-lg font-bold">{pick(language, "दिन", "days")}</span></p>
                  <p className="text-sm text-[#6c7a67] mt-1">{harvest.cropName} {pick(language, "की फसल", "")}</p>
                </>
              ) : <p className="font-display text-2xl font-bold text-[#173f1e] mt-2">—</p>}
              <Link to="/crops" className="mt-auto pt-3 text-[#b06a2c] font-semibold text-sm">{pick(language, "विवरण देखें", "View details")} →</Link>
            </motion.div>
          </div>

          <CashflowCard cashflow={data.cashflow} language={language} item={item} />
          <WeeklyWeather weather={weather} language={language} item={item} />
          <CropCycles cycles={cycles} language={language} item={item} />
          <MyCropRates mycrops={mycrops} language={language} item={item} />

          {/* QUICK ACTIONS 4x2 */}
          <motion.div variants={item}>
            <div className="grid grid-cols-4 gap-2.5" data-testid="quick-actions">
              {QUICK.map((q) => (
                <Link key={q.to} to={q.to} data-testid={`qa-${q.to.slice(1)}`} className="agro-glass rounded-2xl p-2.5 flex flex-col items-center gap-1.5 tap active:scale-95 transition-transform text-center">
                  <span className={`h-11 w-11 rounded-2xl ${q.bg} flex items-center justify-center`}><q.icon className={`h-5 w-5 ${q.fg}`} /></span>
                  <span className="text-[11px] font-semibold text-[#1c2b1e] leading-tight">{pick(language, q.hi, q.en)}</span>
                </Link>
              ))}
            </div>
          </motion.div>

          <FieldsCarousel fields={fields} language={language} item={item} />
        </div>

        {/* RIGHT RAIL — Advanced Alerts & News */}
        <div className="space-y-5 min-w-0">
          <AlertsRail warnings={warnings} language={language} item={item} />

          <motion.div variants={item} className="agro-glass rounded-[22px] p-5" data-testid="mera-season-card">
            <Link to="/season" className="flex items-center gap-3">
              <span className="text-2xl"><PartyPopper className="h-6 w-6 text-[#f2a30f]" /></span>
              <div className="min-w-0"><p className="font-bold text-[#173f1e] text-sm">{pick(language, "आपका सीज़न तैयार है!", "Your season is ready!")}</p><p className="text-[12px] text-[#6c7a67]">{pick(language, "पूरे सीज़न की कहानी देखें", "See your season story")}</p></div>
              <ChevronRight className="h-5 w-5 text-[#2f7d33] ml-auto shrink-0" />
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </PageWrap>
  );
}
